import { useHistory } from '@shared/core';
import { Flex } from '@withjoy/joykit';
import React, { useEffect } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { useEventAccommodationsCheck } from '@shared/hooks/useEventAccommodationsCheck';
import { useGuestSiteQueryParams } from '@apps/guest/packages/layout-engine/common/utils/useGuestSiteQueryParams';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelRoutePaths, UtmParameters } from '../../HotelBooking.routes';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { useQueryParamHelper } from '@shared/core/queryString';
import { PartnerHotelSearchRouteQueryParams } from '../HotelSearch/HotelSearch.types';

export const BookYourStay: React.FC<{ eventHandle: string }> = wrapWithHotelBookingRedirectProvider(({ eventHandle }) => {
  const history = useHistory();
  const { eventInfo } = useEventInfo();
  const { checkIn: eventStartDate, checkOut: eventEndDate } = calculateStayDates(eventInfo?.finalizedDate?.dateString);
  const { onLoadFailure, onLoadComplete } = useHotelBookingRedirectHealth();
  const queryParams = useGuestSiteQueryParams();
  const telemetry = useGuestSiteTelemetry();
  const hotelRoute = useHotelRoutePaths();

  const queryParamHelper = useQueryParamHelper();

  const getQueryParam = (param: PartnerHotelSearchRouteQueryParams) => queryParamHelper.getValueString(param);

  const utmParams = {
    utm_source: getQueryParam(PartnerHotelSearchRouteQueryParams.utm_source),
    utm_medium: getQueryParam(PartnerHotelSearchRouteQueryParams.utm_medium),
    utm_campaign: getQueryParam(PartnerHotelSearchRouteQueryParams.utm_campaign),
    discountCode: getQueryParam(PartnerHotelSearchRouteQueryParams.discountCode)
  };

  const { hasVisibleAccommodationsEventPage, error, hasVisibleTravelEventPage, hasTravelAccommodationListings, isResolved, matchedCustomPageSlug } = useEventAccommodationsCheck({
    eventHandle
  });

  useMount(() => {
    telemetry.bookYourStayPageViewed({
      ...queryParams
    });
  });

  useEffect(() => {
    if (!isResolved) return;

    if (error) {
      onLoadFailure(`BookYourStay: Accommodations visibility check error`, error, {
        url: hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { ...(utmParams as UtmParameters) })
      });
      return;
    }

    const params = new URLSearchParams(utmParams as UtmParameters);

    if (hasVisibleAccommodationsEventPage) {
      onLoadComplete({ url: `/${eventHandle}/accommodations?` + params.toString() });
    } else if (hasVisibleTravelEventPage && hasTravelAccommodationListings) {
      onLoadComplete({ url: `/${eventHandle}/travel?` + params.toString() });
      // Redirect to custom page which have accommodation related matching keyword in page slug
    } else if (matchedCustomPageSlug) {
      onLoadComplete({ url: `/${eventHandle}/page/${matchedCustomPageSlug}?` + params.toString() });
    } else {
      onLoadComplete({
        url: hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { ...(utmParams as UtmParameters) })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, eventEndDate, error, eventStartDate, eventHandle, hasVisibleAccommodationsEventPage, hasVisibleTravelEventPage, hasTravelAccommodationListings, isResolved]);

  return (
    <Flex justifyContent="center" alignItems="center" height="100vh" width="100vw">
      <JoyLogoLoader loaderKey="guest-hotel-booking" />
    </Flex>
  );
});
